import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPrerenderBuild } from '../utils/prerender';
import {
    browserLanguageTags,
    detectCountryCode,
    isCrawlerUserAgent,
    persistPreferredLocale,
    readSavedLocale,
    resolvePreferredLocale,
} from './detectLocale';
import {
    DEFAULT_LOCALE,
    buildAlternateLinks,
    getLocaleFromPathname,
    isSupportedLocale,
    localizedPath,
    SEO_PAGE_LOCALES,
    stripLocaleFromPathname,
} from './locale';
import { COUNTRY_LOCALE_MAP } from '../data/euCountryLanding';
import { isEnglishOnlyCanonicalPath } from './seoPaths';

/** Country doorway paths are EN-canonical but localized body language. */
function getCountryDoorwayContentLocale(pathname = '') {
    const bare = stripLocaleFromPathname(pathname);
    const dedi = bare.match(/^\/services\/dedicated-servers\/([a-z]+)-dedicated-servers$/);
    const vps = bare.match(/^\/services\/vps-kvm\/([a-z]+)-kvm-vps$/);
    const slug = dedi?.[1] || vps?.[1];
    if (!slug) return null;
    return COUNTRY_LOCALE_MAP[slug] || null;
}

const LocaleContext = createContext({
    locale: DEFAULT_LOCALE,
    localizedPath: (path) => path,
    stripLocaleFromPathname: (path) => path,
    buildAlternateLinks: () => [],
    seoLocale: 'en_US',
});

export function useLocale() {
    return useContext(LocaleContext);
}

export function useI18nSeo(canonicalPath) {
    const { locale, localizedPath: lp, buildAlternateLinks: buildLinks } = useLocale();
    const location = useLocation();
    const basePath = canonicalPath || stripLocaleFromPathname(location.pathname);

    return {
        language: locale,
        locale: SEO_PAGE_LOCALES[locale] || SEO_PAGE_LOCALES.en,
        canonical: lp(basePath, locale),
        alternateLinks: buildLinks(basePath),
    };
}

export function LocaleProvider({ children }) {
    const location = useLocation();
    const { i18n } = useTranslation();
    const locale = getLocaleFromPathname(location.pathname);
    const doorwayContentLocale = getCountryDoorwayContentLocale(location.pathname);
    // Prefer UI translations when the doorway language is a supported locale (de/nl/it/fi).
    const translationLocale = (doorwayContentLocale && isSupportedLocale(doorwayContentLocale))
        ? doorwayContentLocale
        : locale;
    // html lang follows the body language even when we lack a full UI locale (e.g. fr/pl).
    const htmlLang = doorwayContentLocale || locale;

    useEffect(() => {
        if (i18n.language !== translationLocale) {
            i18n.changeLanguage(translationLocale);
        }
    }, [i18n, translationLocale]);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.lang = htmlLang;
        }
    }, [htmlLang]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (isPrerenderBuild()) return undefined;
        if (isCrawlerUserAgent(window.navigator?.userAgent)) return undefined;
        if (getLocaleFromPathname(location.pathname) !== DEFAULT_LOCALE) return undefined;

        // English-only money pages (blog, LIR) must stay on the bare EN path.
        // Auto-localizing them fights englishOnlyRoutes / hard redirects and loops.
        const barePath = stripLocaleFromPathname(location.pathname);
        if (isEnglishOnlyCanonicalPath(barePath)) return undefined;

        let cancelled = false;
        const languages = browserLanguageTags();

        const redirectTo = (preferred) => {
            if (cancelled || !preferred || preferred === DEFAULT_LOCALE) return false;
            const nextPath = localizedPath(barePath, preferred);
            if (nextPath === location.pathname) return false;
            persistPreferredLocale(preferred);
            // Full navigation so the document URL matches the locale HTML
            // (SPA navigate leaves Lighthouse mainResource on `/` while
            // Helmet rewrites canonical → “points to another hreflang”).
            const target = `${nextPath}${location.search}${location.hash}`;
            window.location.replace(target);
            return true;
        };

        const saved = readSavedLocale();
        if (saved) {
            redirectTo(saved);
            return undefined;
        }

        const fromBrowser = resolvePreferredLocale({ languages });
        if (redirectTo(fromBrowser)) return undefined;

        detectCountryCode().then((country) => {
            if (cancelled) return;
            const preferred = resolvePreferredLocale({ languages, country });
            if (redirectTo(preferred)) return;
            if (country) persistPreferredLocale(DEFAULT_LOCALE);
        });

        return () => {
            cancelled = true;
        };
    }, [location.hash, location.pathname, location.search]);

    const value = useMemo(() => ({
        locale,
        localizedPath: (path, targetLocale = locale) => localizedPath(path, targetLocale),
        stripLocaleFromPathname,
        buildAlternateLinks,
        seoLocale: SEO_PAGE_LOCALES[locale] || SEO_PAGE_LOCALES.en,
    }), [locale]);

    return (
        <LocaleContext.Provider value={value}>
            {children}
        </LocaleContext.Provider>
    );
}
